import {Renderer, FrameBuffer, Camera, vec3, Material, Texture, Container,  Scene, Mesh, Program, IcoSphereGeometryBuffer, SphereGeometryBuffer,  BoxGeometryBuffer, PlaneGeometryBuffer} from 'beam';
import Tween from './helpers/Tween';
import CubicBezier from './helpers/cubicBezier';
import gridVertexShader from './shaders/grid-vs.glsl'
import gridFragmentShader from './shaders/grid-fs.glsl'

import Box from './Box';

import GLPortfolio from './GLPortfolio';
import GLGlobe from './GLGlobe';
import GLVerticals from './GLVerticals';
import GLWelcomeScreen from './GLWelcomeScreen';
import GLIntroScreen from './GLIntroScreen';
import GLSteppedScroll from './GLSteppedScroll';
import GLSteppedScrollSection from './GLSteppedScrollsection';
import GLBackground from './GLBackground';
import GLBackgroundSphere from './GLBackgroundSphere';

import DenoiserRenderPass from './DenoiserRenderPass'

import App from '../stores/app.js';
import Emitter from '../core/emitter';

const DEBUG_MODE = false;

const SETTINGS = {
    fov:        55,
    resolution: [window.innerWidth, window.innerHeight],
    useTAA:     false,
}

const DPR        = 2;

const USE_GUI = false;
let debugRT = false;
if (USE_GUI) {
    const GUI = document.createElement('input');
    GUI.setAttribute('type', 'checkbox')
    GUI.addEventListener('change', e => {
        debugRT = (GUI.checked);
    })
    GUI.value = -1;
    GUI.style.position = "fixed";
    GUI.style.top = 0;
    GUI.style.top = 0;
    GUI.style.zIndex = 99999999;
    GUI.style.background = 'red';
    document.body.appendChild(GUI);
}   

export default class WebglScene extends HTMLElement {
     
    constructor() {

        super();

        console.log('WEBGL CONSTRUCTOR', this.__isConnected__)

        this.currCameraTilt = 0;
        this._scroll           = 0;
        this._lastScroll       = 0;
        this.userRotationY     = 0;
        this.darkMode = false;
        this.pointer = [0,0]
        this.registeredBoxes = []

        this.resize = this.resize.bind(this);
        this.update = this.update.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);

    }

    registerBox(box) {
        box.resize( SETTINGS.resolution[0], SETTINGS.resolution[1] )
        this.registeredBoxes.push( box );
    }
    unregisterBox(box) {
        for (let i=this.registeredBoxes.length-1; i>=0; i--) {
            if (this.registeredBoxes[i] == box) {
                this.registeredBoxes.splice(i, 1);
                break;
            }
        }
    }

    createBlueNoise() {
        this.bndsTextures = []
        this.bndsTextureIndex = 0;
        for (let i=0; i<8; i++) {
            this.bndsTextures[i] = Texture.fromUrl(this.renderer.gl, "/wp-content/themes/wp-base-theme/dist/assets/textures/noise/bluenoise/256_256/HDR_RGBA_[index].png".replace('[index]',i), {
                linear: false,
                mipmap: false,
            })
        }
    }

    setDarkMode(isDarkMode) {
        this.darkMode = isDarkMode;
    }

    onPointerMove(e) {
        this.pointer = [
            e.pageX,
            e.pageY - window.pageYOffset
        ]
    }

    rotateCameraY(e) {
        this.userRotationY = e
    }

    connectedCallback() {

        console.log('WEBGL CONNECTED')
        this.__isConnected__ = true;
        
        this.renderer = new Renderer({
            alpha:     true,
            antialias: true,
        });
        this.renderer.canvas.style.zIndex   = 0;
        this.renderer.canvas.style.position = 'fixed';
        this.renderer.canvas.style.display  = 'block';
        this.renderer.canvas.style.top      = 0;
        this.renderer.canvas.style.left     = 0;
        this.renderer.canvas.style.height   = 'auto';
        this.renderer.canvas.style.width    = '100%';

        this.insertBefore(this.renderer.canvas, this.firstChild);
        this.maskFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            linear: false,
            type:   this.renderer.gl.FLOAT
        });

        document.addEventListener('mousemove', this.onPointerMove, false)

        this.finalFrameBuffer= new FrameBuffer(this.renderer.gl, {
            width:  1,
            height: 1,
            linear: false,
            type:   this.renderer.gl.FLOAT
        });


        this.temporalAARenderPass    = new DenoiserRenderPass( this.renderer, { 
            useNeighbourgClipping:   false, 
            bilateralBlurIterations: 0,
            spatialThreshold:        100,
            temporalThreshold:       0.98, 
            useReprojection:         false,
            useAtrouNoiseFilter:     false, 
            useTemporalUpscale:      false,
            useVariance:             false, 
            useLongHistory:          false, 
            useDepthCheck:           false,
        })

        this.createBlueNoise();
        
        this.buildScene();
        
        this.resize();
        this.update();
        window.addEventListener('resize', this.resize);
        requestAnimationFrame(()=>{
            App.lenis.on('scroll', e => {
                this._scroll = e.animatedScroll;
            })
        });
    }

    removeChild(node) {
        console.log('REMOVE', node);
        this.scene.remove(node);
    }

    addChild(node) {
        this.scene.add(node);
    }

    disconnectedCallback() {
        console.log('WEBGL DISCONNECTED')
        this.__isConnected__ = false;
        window.removeEventListener('resize', this.resize);
    }

    buildScene() {

        this.blankFrameBuffer = new FrameBuffer(this.renderer.gl, {
            width: 1,
            height: 1,
            linear: true,
        });

        this.scene      = new Scene();
        this.scene.uniforms = {}
        this.scene.defines  = {}

		this.camera = new Camera({
			fov:            SETTINGS.fov,
			near:           0.1,
			far:            100000,
			type:           'perspective',
            moveSpeed:      0.1,
            wheelSpeed:     0.1,
			firstPerson:    false,
            orbitControl:   DEBUG_MODE,
			lookAt:         [0,0,0],
            offset:         [0,0,0],
			position:       [0,0,0], 
            cameraDistance: 400,
        });
        this.camera.position[2] = 400;

        this.bufferCamera = new Camera({
            left:          -1/2,
            right:         1/2,
            top:           1/2,
            bottom:        -1/2,
            near:          1,
            far:           4000.0,
            type:          'ortho',
            orbitControl:  false,
            lookAt:        [0,0,0]
        });
        this.bufferCamera.position[0] = 0.01;
        this.bufferCamera.position[1] = 0.01;
        this.bufferCamera.position[2] = 100;
        this.bufferCamera.lookAt = vec3.create()
        
        this.bufferScene  = new Scene();
        this.bufferPlane  = new Mesh();
        this.bufferPlane.geometry = new PlaneGeometryBuffer(this.renderer.gl, {
            width:  1, 
            height: 1
        });
        this.bufferScene.uniforms = {}
        this.bufferScene.defines  = {}
        
        this.bufferPlane.scale[0] = window.innerWidth
        this.bufferPlane.scale[1] = window.innerHeight
        this.bufferScene.add(this.bufferPlane)
    }

    resize() {

        const width  = window.innerWidth  || 1;
        const height = window.innerHeight || 1;

        SETTINGS.resolution[0] = width  * DPR;
        SETTINGS.resolution[1] = height * DPR;

        this.renderer.setPixelRatio(1);
        this.renderer.resize(SETTINGS.resolution[0], SETTINGS.resolution[1]);
        
        console.log("WEBGL RESIZE", width, height)

        this._cameraZ = (window.innerHeight) / 2 / Math.tan(Math.PI * SETTINGS.fov / 360);
        
        if (!DEBUG_MODE) {
            this.camera.position[2] = this._cameraZ;
        }
        else {
            this.camera._cameraDistance = this._cameraZ;
        }
        
        this.camera.aspect = SETTINGS.resolution[0] / SETTINGS.resolution[1]
        this.camera.updateProjectionMatrix()
        
        this.bufferScene.uniforms.uResolution = [ SETTINGS.resolution[0], SETTINGS.resolution[1] ];
        
        this.maskFrameBuffer.resize(SETTINGS.resolution[0], SETTINGS.resolution[1]);
        this.finalFrameBuffer.resize(SETTINGS.resolution[0], SETTINGS.resolution[1]);
        this.temporalAARenderPass?.resize(SETTINGS.resolution[0], SETTINGS.resolution[1])

        this.bufferPlane.scale[0] = SETTINGS.resolution[0]
        this.bufferPlane.scale[1] = SETTINGS.resolution[1]

        this.registeredBoxes?.forEach(box => box.resize( SETTINGS.resolution[0], SETTINGS.resolution[1] ) );
    }

    update() {

        if (!this.__isConnected__) {
            return;
        }

        requestAnimationFrame(()=>{
            this.update();
        });

        /* TITLE EFFETC */
        // const cameraTilt  = Math.PI/2 + ((this.pointer[0] / window.innerWidth) - 0.5) * 2 * 0.1;
        // const cameraTiltY =   ((this.pointer[1] / window.innerHeight) - 0.5) * 2 * -50;
        // this.currCameraTilt = this.currCameraTilt || 0;
        // this.currCameraTiltY = this.currCameraTiltY || 0;
        // this.currCameraTilt += (cameraTilt - this.currCameraTilt ) * 0.03;
        // this.currCameraTiltY += (cameraTiltY - this.currCameraTiltY ) * 0.03;
        // this.camera.lookAt = [
        //     Math.cos( this.currCameraTilt + Math.PI ) * this._cameraZ/2,
        //     this.currCameraTiltY,
        //     Math.sin( this.currCameraTilt + Math.PI ) * this._cameraZ/2 + this._cameraZ/2
        // ];
        // this.camera.position = [
        //     Math.cos( this.currCameraTilt ) * this._cameraZ/2,
        //     this.currCameraTiltY * -1,
        //     Math.sin( this.currCameraTilt ) * this._cameraZ/2 + this._cameraZ/2
        // ];

        this.camera.update();

        this.registeredBoxes.forEach((box, i) => {
            box.updateBox(this.bufferScene, this.bufferPlane, this.bufferCamera);
        });

        /* Final Render */
        if (this.darkMode) {
            this.currBgColor = this.currBgColor || [26/255, 26/255, 26/255];
            this.currBgColor[0] += ( 26/255 - this.currBgColor[0]) * 0.1
            this.currBgColor[1] += ( 26/255 - this.currBgColor[1]) * 0.1
            this.currBgColor[2] += ( 26/255 - this.currBgColor[2]) * 0.1
        }
        else {
            this.currBgColor = this.currBgColor || [241/255, 241/255, 241/255];
            this.currBgColor[0] += ( 241/255 - this.currBgColor[0]) * 0.1
            this.currBgColor[1] += ( 241/255 - this.currBgColor[1]) * 0.1
            this.currBgColor[2] += ( 241/255 - this.currBgColor[2]) * 0.1
        }

        this.renderer.clearColor(this.currBgColor[0], this.currBgColor[1], this.currBgColor[2], 1);
        this.renderer.clear();
        
        if (SETTINGS.useTAA||debugRT) {
            this.renderer.render(this.scene,  this.camera, this.finalFrameBuffer);
            this.temporalAARenderPass.render( this.bufferScene, this.bufferPlane, this.bufferCamera, this.finalFrameBuffer);//, this.writeHDBuffer, this.readGFrameBuffer, this.lastDepthFrameBuffer);
        }
        else {
            this.renderer.render(this.scene,  this.camera);
            this.bufferPlane.name = 'TAAPass'
        }

        if (!this._ready) {
            this._ready = true;
            window.webglIsReady  = true;
            Emitter.emit('webgl:ready');
        }

        this.scene.uniforms.USE_RT                    = debugRT?1:0;
        this.scene.uniforms.uBlueNoiseResolution      = [256,256]
        this.scene.uniforms.tBNDS                     = this.bndsTextures[ this.bndsTextureIndex ]
        this.bndsTextureIndex++
        this.bndsTextureIndex                           = this.bndsTextureIndex % 8
     
    }
}

customElements.define('webgl-scene', WebglScene)